"use client"

import { useState, useEffect } from "react"
import styled from "styled-components"
import { Search, Star, ArrowUp, ArrowDown } from "react-feather"

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
`

const SearchIcon = styled(Search)`
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: ${(props) => props.theme.secondaryText};
  width: 16px;
  height: 16px;
`

const SearchInput = styled.input`
  width: 100%;
  height: 36px;
  padding: 0 8px 0 32px;
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.border};
  background-color: ${(props) => props.theme.input};
  color: ${(props) => props.theme.text};
  
  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.primary};
  }
`

const TableWrapper = styled.div`
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 4px;
  overflow: hidden;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const TableHead = styled.thead`
  background-color: ${(props) => props.theme.background};
  border-bottom: 1px solid ${(props) => props.theme.border};
`

const TableBody = styled.tbody``

const TableRow = styled.tr`
  &:not(:last-child) {
    border-bottom: 1px solid ${(props) => props.theme.border};
  }
  
  &:hover {
    background-color: ${(props) => props.theme.accent};
  }
`

const TableHeader = styled.th`
  padding: 12px 16px;
  text-align: ${(props) => props.align || "left"};
  font-weight: 500;
  font-size: 14px;
`

const TableCell = styled.td`
  padding: 12px 16px;
  text-align: ${(props) => props.align || "left"};
  font-size: 14px;
`

const SortButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  font-weight: 500;
  font-size: 14px;
  color: ${(props) => props.theme.text};
  cursor: pointer;
  
  &:hover {
    color: ${(props) => props.theme.primary};
  }
`

const FavoriteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${(props) => (props.favorited ? "#F59E0B" : props.theme.muted)};
  
  &:hover {
    color: ${(props) => (props.favorited ? "#F59E0B" : props.theme.text)};
  }
`

const CoinInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const CoinName = styled.div`
  font-weight: 500;
`

const CoinSymbol = styled.div`
  font-size: 12px;
  color: ${(props) => props.theme.secondaryText};
`

const PriceText = styled.div`
  font-weight: 500;
`

const ChangeText = styled.div`
  color: ${(props) => (props.positive ? props.theme.success : props.theme.error)};
`

function CoinTable() {
  const [coins, setCoins] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [sortConfig, setSortConfig] = useState({
    key: "market_cap",
    direction: "descending",
  })

  // Simulate fetching coin data
  useEffect(() => {
    // This would be replaced with actual API calls
    const mockData = [
      {
        id: "bitcoin",
        name: "Bitcoin",
        symbol: "BTC",
        current_price: 43256.78,
        price_change_percentage_24h: 2.34,
        market_cap: 834567890123,
        total_volume: 23456789012,
        low_24h: 42100.0,
        high_24h: 43500.0,
        favorited: false,
        kimchi_premium: 2.5,
      },
      {
        id: "ethereum",
        name: "Ethereum",
        symbol: "ETH",
        current_price: 2345.92,
        price_change_percentage_24h: 1.21,
        market_cap: 278901234567,
        total_volume: 12345678901,
        low_24h: 2300.0,
        high_24h: 2350.0,
        favorited: true,
        kimchi_premium: 1.8,
      },
      {
        id: "ripple",
        name: "XRP",
        symbol: "XRP",
        current_price: 0.5678,
        price_change_percentage_24h: -0.45,
        market_cap: 27890123456,
        total_volume: 1234567890,
        low_24h: 0.56,
        high_24h: 0.57,
        favorited: false,
        kimchi_premium: 0.9,
      },
      {
        id: "cardano",
        name: "Cardano",
        symbol: "ADA",
        current_price: 0.3456,
        price_change_percentage_24h: 0.67,
        market_cap: 12345678901,
        total_volume: 567890123,
        low_24h: 0.34,
        high_24h: 0.35,
        favorited: false,
        kimchi_premium: 1.2,
      },
      {
        id: "solana",
        name: "Solana",
        symbol: "SOL",
        current_price: 98.76,
        price_change_percentage_24h: 3.21,
        market_cap: 45678901234,
        total_volume: 3456789012,
        low_24h: 95.0,
        high_24h: 99.0,
        favorited: false,
        kimchi_premium: 2.1,
      },
    ]

    setCoins(mockData)
  }, [])

  // Sort coins
  const sortedCoins = [...coins].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "ascending" ? -1 : 1
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "ascending" ? 1 : -1
    }
    return 0
  })

  // Filter coins by search term
  const filteredCoins = sortedCoins.filter(
    (coin) =>
      coin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coin.symbol.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  // Toggle sort
  const requestSort = (key) => {
    let direction = "ascending"
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending"
    }
    setSortConfig({ key, direction })
  }

  // Toggle favorite
  const toggleFavorite = (id) => {
    setCoins(coins.map((coin) => (coin.id === id ? { ...coin, favorited: !coin.favorited } : coin)))
  }

  return (
    <TableContainer>
      <SearchContainer>
        <SearchInputWrapper>
          <SearchIcon />
          <SearchInput
            placeholder="Search coins..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInputWrapper>
      </SearchContainer>

      <TableWrapper>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader style={{ width: "40px" }}></TableHeader>
              <TableHeader>
                <SortButton onClick={() => requestSort("name")}>
                  Name
                  {sortConfig.key === "name" &&
                    (sortConfig.direction === "ascending" ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                </SortButton>
              </TableHeader>
              <TableHeader align="right">
                <SortButton onClick={() => requestSort("current_price")}>
                  Price
                  {sortConfig.key === "current_price" &&
                    (sortConfig.direction === "ascending" ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                </SortButton>
              </TableHeader>
              <TableHeader align="right">
                <SortButton onClick={() => requestSort("kimchi_premium")}>
                  Kimchi Premium
                  {sortConfig.key === "kimchi_premium" &&
                    (sortConfig.direction === "ascending" ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                </SortButton>
              </TableHeader>
              <TableHeader align="right">
                <SortButton onClick={() => requestSort("price_change_percentage_24h")}>
                  24h %
                  {sortConfig.key === "price_change_percentage_24h" &&
                    (sortConfig.direction === "ascending" ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                </SortButton>
              </TableHeader>
              <TableHeader align="right" className="hidden-mobile">
                <SortButton onClick={() => requestSort("market_cap")}>
                  Market Cap
                  {sortConfig.key === "market_cap" &&
                    (sortConfig.direction === "ascending" ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                </SortButton>
              </TableHeader>
              <TableHeader align="right" className="hidden-mobile">
                <SortButton onClick={() => requestSort("total_volume")}>
                  Volume (24h)
                  {sortConfig.key === "total_volume" &&
                    (sortConfig.direction === "ascending" ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                </SortButton>
              </TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCoins.map((coin) => (
              <TableRow key={coin.id}>
                <TableCell>
                  <FavoriteButton favorited={coin.favorited} onClick={() => toggleFavorite(coin.id)}>
                    <Star size={16} fill={coin.favorited ? "#F59E0B" : "none"} />
                  </FavoriteButton>
                </TableCell>
                <TableCell>
                  <CoinInfo>
                    <CoinName>{coin.name}</CoinName>
                    <CoinSymbol>{coin.symbol}</CoinSymbol>
                  </CoinInfo>
                </TableCell>
                <TableCell align="right">
                  <PriceText>${coin.current_price.toLocaleString()}</PriceText>
                </TableCell>
                <TableCell align="right">
                  <ChangeText positive={coin.kimchi_premium > 0}>{coin.kimchi_premium.toFixed(2)}%</ChangeText>
                </TableCell>
                <TableCell align="right">
                  <ChangeText positive={coin.price_change_percentage_24h > 0}>
                    {coin.price_change_percentage_24h > 0 ? "+" : ""}
                    {coin.price_change_percentage_24h.toFixed(2)}%
                  </ChangeText>
                </TableCell>
                <TableCell align="right" className="hidden-mobile">
                  ${(coin.market_cap / 1000000000).toFixed(2)}B
                </TableCell>
                <TableCell align="right" className="hidden-mobile">
                  ${(coin.total_volume / 1000000000).toFixed(2)}B
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableWrapper>
    </TableContainer>
  )
}

export default CoinTable

